import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Users, Check, Lock, UserPlus } from "lucide-react";
import { useChit } from "@/context/ChitContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ecranul de alăturare la un grup, deschis dintr-un link de invitație.
export default function Join() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { joinGroup } = useChit();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/groups/${groupId}/preview`, {
          withCredentials: true,
        });
        if (r.data.alreadyMember) {
          navigate(`/grup/${groupId}`, { replace: true });
          return;
        }
        setPreview(r.data);
      } catch {
        setErr(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const doJoin = async (mode, memberId = null) => {
    setBusy(true);
    try {
      await joinGroup(groupId, mode, memberId);
      toast.success("Te-ai alăturat grupului!");
      navigate(`/grup/${groupId}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la alăturare");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (err || !preview) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-8 text-center">
        <p className="text-slate-500">Linkul de invitație nu e valid.</p>
        <Button
          onClick={() => navigate("/")}
          className="mt-4 rounded-xl bg-emerald-600 hover:bg-emerald-700"
        >
          Acasă
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 py-8">
      <div className="flex flex-col items-center text-center mb-8">
        <div className="h-16 w-16 rounded-3xl bg-emerald-100 flex items-center justify-center mb-4">
          <Users className="h-8 w-8 text-emerald-600" strokeWidth={2.5} />
        </div>
        <p className="text-sm text-slate-500">Ai fost invitat în grupul</p>
        <h1 className="font-heading text-2xl font-black text-slate-900 mt-1">
          {preview.name}
        </h1>
      </div>

      <p className="text-sm font-semibold text-slate-700 mb-3">
        Care membru ești tu?
      </p>
      <div className="space-y-2">
        {preview.members.map((m) => (
          <button
            key={m.id}
            data-testid={`claim-member-${m.id}`}
            disabled={m.claimed || busy}
            onClick={() => doJoin("claim", m.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-colors text-left ${
              m.claimed
                ? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed"
                : "border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50"
            }`}
          >
            <Avatar name={m.name} />
            <span className="flex-1 font-semibold text-slate-800">{m.name}</span>
            {m.claimed ? (
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Lock className="h-3.5 w-3.5" /> revendicat
              </span>
            ) : (
              <Check className="h-5 w-5 text-emerald-500" strokeWidth={2.5} />
            )}
          </button>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-slate-100">
        <button
          data-testid="join-as-new-btn"
          disabled={busy}
          onClick={() => doJoin("new")}
          className="w-full flex items-center justify-center gap-2 p-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors"
        >
          <UserPlus className="h-5 w-5" strokeWidth={2.5} />
          Nu-s în listă — adaugă-mă ca membru nou
        </button>
      </div>
    </div>
  );
}
